<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-2">
      <div class="d-flex align-center ga-2">
        <v-icon size="18" color="deep-purple">mdi-table-sync</v-icon>
        <div class="text-subtitle-2 text-capitalize">{{ sectionName }}</div>
        <v-chip size="x-small" color="deep-purple" variant="tonal">
          {{ rows.length }} row{{ rows.length !== 1 ? 's' : '' }}
        </v-chip>
      </div>
      <div class="d-flex align-center ga-1">
        <v-btn variant="text" size="x-small" color="deep-purple" @click="$emit('add-column')">
          <v-icon start size="14">mdi-table-column-plus-after</v-icon>
          Add Column
        </v-btn>
      </div>
    </div>
    <div class="text-caption text-medium-emphasis mb-3">
      Repeating section — add or remove rows as needed. Each row will generate a separate entry in the document.
    </div>

    <!-- Row tabs (when multiple rows) -->
    <div v-if="rows.length > 1" class="d-flex align-center ga-2 mb-3 flex-wrap">
      <v-btn
        v-for="(_, rowIdx) in rows"
        :key="rowIdx"
        :variant="rowIdx === activeRow ? 'flat' : 'outlined'"
        :color="rowIdx === activeRow ? 'success' : undefined"
        size="small"
        @click="activeRow = rowIdx"
      >
        #{{ rowIdx + 1 }}
      </v-btn>
      <v-btn variant="text" size="small" color="primary" @click="$emit('add-row')">
        <v-icon size="16">mdi-plus</v-icon>
      </v-btn>
    </div>

    <!-- Vertical form layout (per row) -->
    <div class="vertical-form">
      <div
        v-for="(col, colIdx) in columns"
        :key="col.varKey"
        class="vertical-row"
      >
        <div class="vertical-label">
          <input
            class="header-input"
            :value="formatLabel(col.header)"
            :title="`varKey: ${col.varKey}`"
            @input="(e: Event) => $emit('update-header', colIdx, (e.target as HTMLInputElement).value)"
            @keyup.enter="(e: Event) => (e.target as HTMLInputElement).blur()"
          />
          <v-btn
            v-if="isCustomColumn(col)"
            icon
            variant="text"
            size="x-small"
            class="label-del label-del--visible"
            :aria-label="`Remove column ${formatLabel(col.header)}`"
            @click.stop="$emit('remove-column', colIdx)"
          >
            <v-icon size="14" color="error">mdi-delete-outline</v-icon>
            <v-tooltip activator="parent" location="top">Remove column</v-tooltip>
          </v-btn>
        </div>
        <div class="vertical-value">
          <v-text-field
            :model-value="currentRow?.[col.varKey] || ''"
            variant="outlined"
            density="compact"
            hide-details
            :placeholder="col.varLabel"
            @update:model-value="(val: string) => $emit('update-cell', activeRow, col.varKey, val)"
          >
            <template #append-inner>
              <div
                v-if="!currentRow?.[col.varKey]"
                :class="['ai-icon-btn', isAnalyzed(col.varKey) ? 'ai-icon-btn--analyzed' : '']"
                @click.stop="$emit('field-click', activeRow, col.varKey, col.header)"
              >
                <v-progress-circular
                  v-if="isAnalyzing(activeRow, col.varKey)"
                  indeterminate
                  size="14"
                  width="2"
                  color="primary"
                />
                <v-icon v-else size="16" color="white">
                  {{ getFieldIcon(col.varKey) }}
                </v-icon>
                <v-tooltip activator="parent" location="top">
                  {{ isAnalyzed(col.varKey) ? 'AI analyzed — click to fill' : 'AI will analyze and suggest a value' }}
                </v-tooltip>
              </div>
            </template>
          </v-text-field>
        </div>
      </div>
    </div>

    <!-- Bottom actions -->
    <div class="d-flex align-center ga-2 mt-2">
      <v-btn
        variant="tonal"
        size="small"
        color="primary"
        @click="$emit('add-row')"
      >
        <v-icon start size="16">mdi-table-plus</v-icon>
        Add Table
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="rows.length > 1"
        variant="text"
        size="small"
        color="error"
        @click="$emit('remove-row', activeRow); if (activeRow > 0) activeRow--"
      >
        <v-icon start size="16">mdi-delete-outline</v-icon>
        Remove #{{ activeRow + 1 }}
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import type { RepeatingSectionColumn, RepeatingSectionRowData, PlaceholderAnalysis } from '../types/docGen.types';
import { isCustomRepeatingColumn } from '../utils/repeatingSectionColumns';

const props = defineProps<{
  sectionName: string;
  columns: RepeatingSectionColumn[];
  rows: RepeatingSectionRowData[];
  analyzingKey?: string | null;
  analysisMap?: Record<string, PlaceholderAnalysis>;
}>();

defineEmits<{
  'update-cell': [rowIndex: number, varKey: string, value: string];
  'update-header': [colIndex: number, newHeader: string];
  'add-row': [];
  'remove-row': [rowIndex: number];
  'add-column': [];
  'remove-column': [colIndex: number];
  'field-click': [rowIndex: number, varKey: string, header: string];
}>();

const activeRow = ref(0);
const lastRowCount = ref<number>(props.rows.length || 0);

const currentRow = computed(() => props.rows[activeRow.value]);

watch(
  () => props.rows.length,
  (len) => {
    // If a row was added, auto-select the newest row.
    if (len > lastRowCount.value) {
      activeRow.value = Math.max(0, len - 1);
    }
    // If a row was removed and activeRow is out of range, clamp.
    if (activeRow.value >= len) activeRow.value = Math.max(0, len - 1);
    lastRowCount.value = len;
  }
);

function isCustomColumn(col: RepeatingSectionColumn): boolean {
  return isCustomRepeatingColumn(col);
}

function isAnalyzing(rowIdx: number, varKey: string): boolean {
  return props.analyzingKey === `${rowIdx}:${varKey}`;
}

function isAnalyzed(varKey: string): boolean {
  return !!props.analysisMap?.[varKey];
}

/** 라벨 포맷: director_name → Director Name */
function formatLabel(header: string): string {
  if (!header) return '';
  // 이미 사람이 편집한 라벨(공백 포함, 대문자 시작)이면 그대로
  if (/\s/.test(header) && /^[A-Z]/.test(header)) return header;
  // snake_case → Title Case
  return header
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/** varKey 기반 아이콘 — analysisMap보다 varKey 패턴 매칭 우선 */
function getFieldIcon(varKey: string): string {
  const key = varKey.toLowerCase();
  if (/nationality|country/.test(key)) return 'mdi-earth';
  if (/address|street|city|state|post_code/.test(key)) return 'mdi-map-marker';
  if (/date|dob|became|ceased/.test(key)) return 'mdi-calendar';
  if (/id_no|nric|ic_no|passport|identity|reg_no/.test(key)) return 'mdi-card-account-details';
  if (/email/.test(key)) return 'mdi-email-outline';
  if (/mobile|tel|phone/.test(key)) return 'mdi-phone';
  if (/shares|capital|folio/.test(key)) return 'mdi-chart-pie';
  if (/name|aliases/.test(key)) return 'mdi-account';
  if (/position|role|title/.test(key)) return 'mdi-briefcase';
  // analysisMap fallback
  const analysis = props.analysisMap?.[varKey];
  if (analysis) {
    switch (analysis.sourceResolver) {
      case 'ENTITY': return 'mdi-domain';
      case 'DATE': return 'mdi-calendar';
      case 'NUMBER': return 'mdi-numeric';
      case 'SELECT': return 'mdi-form-dropdown';
      case 'OFFICER': return 'mdi-account';
    }
  }
  return 'mdi-auto-fix';
}

</script>

<style scoped>
.vertical-form {
  border: 1px solid rgba(var(--v-theme-primary), 0.18);
  border-radius: 10px;
  overflow: hidden;
}

.vertical-row {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid rgba(var(--v-theme-primary), 0.10);
}
.vertical-row:last-child {
  border-bottom: none;
}
.vertical-row:nth-child(even) .vertical-label {
  background: rgba(var(--v-theme-primary), 0.04);
}
.vertical-row:nth-child(odd) .vertical-label {
  background: rgba(var(--v-theme-primary), 0.08);
}
.vertical-row:hover .vertical-label {
  background: rgba(var(--v-theme-primary), 0.13);
}

.vertical-label {
  width: 220px;
  min-width: 220px;
  padding: 6px 4px 6px 12px;
  display: flex;
  align-items: center;
  position: relative;
  border-right: 2px solid rgba(var(--v-theme-primary), 0.2);
  transition: background 0.15s;
}

.vertical-value {
  flex: 1;
  padding: 4px;
}

.header-input {
  width: 100%;
  padding: 4px 28px 4px 0;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.85);
  outline: none;
  cursor: text;
  letter-spacing: 0.01em;
}
.header-input:focus {
  background: rgba(var(--v-theme-primary), 0.06);
  color: rgb(var(--v-theme-on-surface));
  border-radius: 4px;
}

.label-del {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  flex-shrink: 0;
}
.label-del--visible {
  opacity: 1;
}

.ai-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  cursor: pointer;
  transition: transform 0.1s;
  flex-shrink: 0;
}
.ai-icon-btn:hover {
  transform: scale(1.1);
}
/* 분석 완료된 필드: 초록색 아이콘 */
.ai-icon-btn--analyzed {
  background: #4caf50;
}
</style>
